// components/universal/GoogleMapInput.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "25vh",
};

const defaultCenter = { lat: 40.7128, lng: -74.0060 };
const googleMapsLibraries = ["places"];

// Utility to validate coordinates
const isValidLatLng = (lat, lng) =>
  typeof lat === "number" && typeof lng === "number" && isFinite(lat) && isFinite(lng);

const parseLocation = (loc) => {
  if (!loc) return defaultCenter;
  const lat = parseFloat(loc.lat);
  const lng = parseFloat(loc.lng);
  return isValidLatLng(lat, lng) ? { lat, lng } : defaultCenter;
};

const isSamePosition = (a, b) =>
  Boolean(a && b) && Number(a.lat) === Number(b.lat) && Number(a.lng) === Number(b.lng);

const autocompleteFields = ["geometry", "formatted_address"];

const GoogleMapInput = ({ onLocationChange, defaultLocation }) => {
  const parsedDefaultLocation = useMemo(
    () => parseLocation(defaultLocation),
    [defaultLocation?.lat, defaultLocation?.lng]
  );
  const lastSyncedDefaultRef = useRef(null);
  const [markerPosition, setMarkerPosition] = useState(() => parsedDefaultLocation);
  const [mapRef, setMapRef] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const inputRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCyycvdGXNMh9vaRmb1FeHzcU5NxrcioWc",
    libraries: googleMapsLibraries,
  });

  // Update only when coordinates actually change. Parent forms often pass a new object each render.
  useEffect(() => {
    if (isSamePosition(lastSyncedDefaultRef.current, parsedDefaultLocation)) return;

    lastSyncedDefaultRef.current = parsedDefaultLocation;
    setMarkerPosition((prevPosition) =>
      isSamePosition(prevPosition, parsedDefaultLocation) ? prevPosition : parsedDefaultLocation
    );

    if (mapRef && isValidLatLng(parsedDefaultLocation.lat, parsedDefaultLocation.lng)) {
      mapRef.panTo(parsedDefaultLocation);
    }
  }, [parsedDefaultLocation, mapRef]);

  const onMapLoad = useCallback((map) => setMapRef(map), []);

  // ✅ Move marker when a place is selected
  const onPlaceChanged = () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place || !place.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const address = place.formatted_address || inputRef.current.value;

    const newPosition = { lat, lng };
    setMarkerPosition(newPosition);
    onLocationChange({ lat, lng, address });

    if (mapRef) mapRef.panTo(newPosition);
  };

  // ✅ Handle pressing Enter manually
  const handleManualEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const query = e.target.value.trim();
      if (!query) return;

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: query }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          const address = results[0].formatted_address;
          const newPosition = { lat, lng };

          setMarkerPosition(newPosition);
          onLocationChange({ lat, lng, address });
          if (mapRef) mapRef.panTo(newPosition);
        }
      });
    }
  };

  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      const address =
        status === "OK" && results[0] ? results[0].formatted_address : "";
      onLocationChange({ lat, lng, address });
    });

    setMarkerPosition({ lat, lng });
  };

  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <div className="w-full mt-4">
      {/* ✅ Attach Autocomplete properly */}
      <Autocomplete
        onLoad={(ac) => setAutocomplete(ac)}
        onPlaceChanged={onPlaceChanged}
        fields={autocompleteFields}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder="Search for a location"
          onKeyDown={handleManualEnter}
          className="bg-blackish p-2 w-full text-white text-[14px] font-medium mt-2 leading-4 rounded-md h-10"
        />
      </Autocomplete>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={markerPosition}
        zoom={12}
        onLoad={onMapLoad}
      >
        <Marker
          position={markerPosition}
          draggable
          onDragEnd={handleMarkerDragEnd}
        />
      </GoogleMap>
    </div>
  );
};

export default React.memo(GoogleMapInput);
